import { combineReducers, Reducer } from "redux";
import {
  AdminAction,
  CLEAR_USER_MANAGEMENT_LOADING_LABEL,
  CREATE_EMPLOYEE,
  CREATE_EMPLOYEE_SUCCESS,
  CREATE_USER,
  CREATE_USER_SUCCESS,
  DELETE_USER,
  DELETE_USER_SUCCESS,
  LOAD_ALL_USERS,
  LOAD_ALL_USERS_SUCCESS,
  RESEND_USER_INVITE_SUCCESS,
  REINSTATE_USER_SUCCESS,
  UPDATE_USER,
  UPDATE_USER_SUCCESS,
  REVOKE_WITH_REASSIGNMENT,
  REVOKE_WITH_REASSIGNMENT_SUCCESS,
  GENERATE_INVITATION_LINK,
  GENERATE_INVITATION_LINK_SUCCESS,
  CLEAR_INVITATION_LINK,
} from "./actions";
import { User } from "@amsanalytics/react-shared-library";

export interface IAdminState {
  users: User[];
  userManagementPageLoadingLabel: string | null; // if null, nothing loading
  invitationLinkUrl: string | null; // resolved guest invitation redeem link; when set, the copy-link dialog is shown
  invitationLinkLoading: boolean; // true while the /invitation-link request is in flight (dialog shows a spinner)
}
export const INITIAL_STATE: IAdminState = {
  users: [],
  userManagementPageLoadingLabel: null,
  invitationLinkUrl: null,
  invitationLinkLoading: false
}

const usersReducer = (state: User[] = INITIAL_STATE.users, action: AdminAction): User[] => {
  switch (action.type) {
    case LOAD_ALL_USERS_SUCCESS:
      return action.payload.users;
    case CREATE_USER_SUCCESS:
      if (action.payload.result.success && action.payload.result.user) {
        return [...state, { ...action.payload.result.user, isActive: (action.payload.result.user?.isCrmUser || action.payload.result.user?.isLosUser), hasEmployeeRecord: (action.payload.result.user?.isCrmUser || action.payload.result.user?.isLosUser) }];
      }
      return state;
    case CREATE_EMPLOYEE_SUCCESS:
      return state.map(u => {
        if (u.adUserId.toUpperCase() === action.payload.result.user?.adUserId?.toUpperCase()) {
          return {
            ...u,
            hasEmployeeRecord: true,
            isActive: action.payload.result.user?.isActive
          }
        } else {
          return u;
        }
      });
    case UPDATE_USER_SUCCESS:
      return state.map(u => {
        if (u.userId === action.payload.user.userId) {
          return {
            ...u,
            ...action.payload.user
          }
        } else {
          return u;
        }
      });
    case RESEND_USER_INVITE_SUCCESS:
    case REINSTATE_USER_SUCCESS:
      return state.map(u => {
        if (u.adUserId === action.payload.adUserId) {
          return {
            ...u,
            isActive: true
          }
        }
        return u;
      })
    case DELETE_USER_SUCCESS:
    case REVOKE_WITH_REASSIGNMENT_SUCCESS:
      return state.map(u => {
        if (u.adUserId === action.payload.adUserId) {
          return {
            ...u,
            isActive: false
          }
        }
        return u;
      })
    default:
      return state;
  }
};

const userManagementPageLoadingLabelReducer = (state: string | null = INITIAL_STATE.userManagementPageLoadingLabel, action: any): string | null => {
  switch (action.type) {
    case LOAD_ALL_USERS:
      return "Loading Users...";
    case LOAD_ALL_USERS_SUCCESS:
      return null;
      case CREATE_USER:
      return "Creating User...";
    case CREATE_USER_SUCCESS:
      return null;
    case UPDATE_USER:
      return "Updating User...";
    case UPDATE_USER_SUCCESS:
      return null;
    case DELETE_USER:
    case REVOKE_WITH_REASSIGNMENT:
      return "Revoking User...";
    case DELETE_USER_SUCCESS:
    case REVOKE_WITH_REASSIGNMENT_SUCCESS:
      return null;
    case CREATE_EMPLOYEE:
      return "Creating Employee...";
    case CREATE_EMPLOYEE_SUCCESS:
      return null;
    case CLEAR_USER_MANAGEMENT_LOADING_LABEL:
      return null;
    default:
      return state;
  }
}


const invitationLinkUrlReducer = (state: string | null = INITIAL_STATE.invitationLinkUrl, action: any): string | null => {
  switch (action.type) {
    case GENERATE_INVITATION_LINK:
    case CLEAR_INVITATION_LINK:
      return null;
    case GENERATE_INVITATION_LINK_SUCCESS:
      return action.payload.url;
    default:
      return state;
  }
}

const invitationLinkLoadingReducer = (state: boolean = INITIAL_STATE.invitationLinkLoading, action: any): boolean => {
  switch (action.type) {
    case GENERATE_INVITATION_LINK:
      return true;
    case GENERATE_INVITATION_LINK_SUCCESS:
    case CLEAR_INVITATION_LINK:
      return false;
    default:
      return state;
  }
}

export const adminReducer: Reducer<IAdminState> = combineReducers<IAdminState>({
  users: usersReducer,
  userManagementPageLoadingLabel: userManagementPageLoadingLabelReducer,
  invitationLinkUrl: invitationLinkUrlReducer,
  invitationLinkLoading: invitationLinkLoadingReducer
});
